import axios from 'axios';
import {
  ConfigurationChangeModel,
  CopyReferenceModel,
  CabinetConfigurationModel,
  CabinetConfigurationSettingsModel,
  DeleteConfigurationModel,
  CabinetModel,
  NotificationModel
} from '@/types';
import AxiosConfiguration from '@/classes/AxiosConfiguration';
import { CopyCabinetReferencesModel } from '@/types/models/configuration/cabinet/CopyCabinetReferencesModel';
import { DeleteConfigurationResponseModel } from '@/types/models/configuration/cabinet/DeleteConfigurationResponseModel';
import { DocumentLinkConfigurationModel } from '@/types/models/configuration/cabinet/DocumentLinkConfigurationModel';

const CabinetConfigurationApiUrl = 'cabinetConfiguration';
const DocumentLinkApiRouteUrl = 'documentLink';
export const CabinetConfigurationService = {
  async beginChangeAsync(change: ConfigurationChangeModel): Promise<ConfigurationChangeModel> {
    const response = await axios.post(`${CabinetConfigurationApiUrl}/change`, change);
    return response.data;
  },
  async endChangeAsync(changeId: number): Promise<ConfigurationChangeModel> {
    const response = await axios.put(`${CabinetConfigurationApiUrl}/change/${changeId}`);
    return response.data;
  },
  async getChangeWithErrorPageAsync(): Promise<ConfigurationChangeModel> {
    const response = await AxiosConfiguration.axiosWithErrorPage.get(`${CabinetConfigurationApiUrl}/change`);
    return response.data;
  },
  async getAllCabinetReferences(): Promise<CopyReferenceModel[]> {
    const response = await axios.get(`${CabinetConfigurationApiUrl}/allcabinetreferences`);
    return response.data;
  },
  async getAllCabinets(): Promise<CopyCabinetReferencesModel[]> {
    const response = await axios.get(`${CabinetConfigurationApiUrl}/allcabinets`);
    return response.data;
  },
  async getAllCabinetsData(): Promise<CabinetModel[]> {
    const response = await axios.get(`${CabinetConfigurationApiUrl}/allcabinetsdata`);
    return response.data;
  },
  async getAllCabinetNames(): Promise<string[]> {
    const response = await axios.get(`${CabinetConfigurationApiUrl}/allcabinetnames`);
    return response.data;
  },
  async getCabinetReferenceConfiguration(referenceId: number): Promise<CabinetConfigurationModel> {
    const response = await axios.get(`${CabinetConfigurationApiUrl}/cabinetreferenceconfiguration/${referenceId}`);
    return response.data;
  },
  async getCabinetConfigurationAsync(cabinetId: string): Promise<CabinetConfigurationModel> {
    const response = await axios.get(`${CabinetConfigurationApiUrl}/${cabinetId}`);
    return response.data;
  },
  async getCabinetConfigurationWithErrorPageAsync(cabinetId: string): Promise<CabinetConfigurationModel> {
    const response = await AxiosConfiguration.axiosWithErrorPage.get(`${CabinetConfigurationApiUrl}/${cabinetId}`);
    return response.data;
  },
  async createCabinetAsync(cabinetConfiguration: CabinetConfigurationModel): Promise<CabinetConfigurationModel> {
    const response = await axios.post(CabinetConfigurationApiUrl, cabinetConfiguration);
    return response.data;
  },
  async updateCabinetAsync(cabinetConfiguration: CabinetConfigurationModel): Promise<CabinetConfigurationModel> {
    const response = await axios.put(CabinetConfigurationApiUrl, cabinetConfiguration);
    return response.data;
  },
  async deleteCabinetAsync(deleteConfiguration: DeleteConfigurationModel): Promise<DeleteConfigurationResponseModel> {
    const response = await axios.delete(CabinetConfigurationApiUrl, { data: deleteConfiguration });
    return response.data;
  },
  async createDocumentTypeAsync(cabinetConfiguration: CabinetConfigurationModel): Promise<CabinetConfigurationModel> {
    const response = await axios.post(`${CabinetConfigurationApiUrl}/documentType`, cabinetConfiguration);
    return response.data;
  },
  async updateDocumentTypeAsync(cabinetConfiguration: CabinetConfigurationModel): Promise<CabinetConfigurationModel> {
    const response = await axios.put(`${CabinetConfigurationApiUrl}/documentType`, cabinetConfiguration);
    return response.data;
  },
  async deleteDocumentTypeAsync(
    deleteDocumentTypeConfiguration: DeleteConfigurationModel
  ): Promise<DeleteConfigurationResponseModel> {
    const response = await axios.delete(`${CabinetConfigurationApiUrl}/documentType`, {
      data: deleteDocumentTypeConfiguration
    });
    return response.data;
  },
  async checkAuthorizedWithErrorPageAsync(): Promise<ConfigurationChangeModel> {
    const response = await AxiosConfiguration.axiosWithErrorPage.get(`${CabinetConfigurationApiUrl}/checkAuthorized`);
    return response.data;
  },
  async getCabinetConfigurationSettingsWithErrorPageAsync(): Promise<CabinetConfigurationSettingsModel> {
    const response = await AxiosConfiguration.axiosWithErrorPage.get(`${CabinetConfigurationApiUrl}/settings`);
    return response.data;
  },
  async createDocumentLinkAsync(
    documentLinkConfiguration: DocumentLinkConfigurationModel,
    bidirectional: boolean
  ): Promise<CabinetConfigurationSettingsModel> {
    const response = await axios.post(
      // tslint:disable-next-line:max-line-length
      `${CabinetConfigurationApiUrl}/${DocumentLinkApiRouteUrl}${bidirectional ? 'Bidirectional' : ''}`,
      documentLinkConfiguration
    );
    return response.data;
  },
  async checkExistingBidirectionalDocumentLink(
    documentLinkConfiguration: DocumentLinkConfigurationModel
  ): Promise<boolean> {
    const response = await axios.post(
      `${CabinetConfigurationApiUrl}/checkExistingBidirectionalDocumentLink`,
      documentLinkConfiguration
    );
    return response.data;
  },
  async updateDocumentLinkAsync(
    documentLinkConfiguration: DocumentLinkConfigurationModel
  ): Promise<CabinetConfigurationSettingsModel> {
    const response = await axios.put(
      `${CabinetConfigurationApiUrl}/${DocumentLinkApiRouteUrl}`,
      documentLinkConfiguration
    );
    return response.data;
  },
  async deleteDocumentLinkAsync(
    deleteConfiguration: DeleteConfigurationModel
  ): Promise<DeleteConfigurationResponseModel> {
    const response = await axios.delete(`${CabinetConfigurationApiUrl}/${DocumentLinkApiRouteUrl}`, {
      data: deleteConfiguration
    });
    return response.data;
  }
};
